import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, productImage, CATEGORY_META } from "../lib/api";
import { CheckCircle2, ArrowLeft, ShoppingCart, Loader2, Download, Mail, FileText } from "lucide-react";
import { RequestManual } from "../components/RequestManual";
import { ManualInfo } from "../components/ManualInfo";
import { CATEGORY_CONTENT } from "../data/manualContent";
import { Seo, BASE } from "../components/Seo";

export default function Category() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = CATEGORY_META[slug];

  useEffect(() => {
    setLoading(true);
    api.get(`/products?category=${slug}`)
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, [slug]);

  const catName = meta ? meta.name : slug;
  const content = CATEGORY_CONTENT[slug];
  const product = products[0];
  const productImg = product ? productImage(product) : null;
  const productJsonLd = product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.title || `Manual för ${catName}`,
        description: product.description || `Pedagogisk PDF-manual för ${catName}.`,
        ...(productImg ? { image: productImg } : {}),
        brand: { "@type": "Brand", name: "Enkla Manualer" },
        category: "Digital manual",
        ...(product.id ? { sku: String(product.id) } : {}),
        offers: {
          "@type": "Offer",
          price: String(Math.round(product.price)),
          priceCurrency: "SEK",
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
          url: `${BASE}/manual/${slug}`,
        },
      }
    : null;

  const faqJsonLd = content
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: content.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const jsonLdBlocks = [productJsonLd, faqJsonLd].filter(Boolean);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title={`Manual för ${catName}`}
        description={`Enkel, pedagogisk manual för ${catName} i PDF-format. Stor text och tydliga bilder – ladda ner direkt efter köp och läs på mobil, surfplatta eller dator.`}
        path={`/manual/${slug}`}
        jsonLd={jsonLdBlocks}
      />
      <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6">
        Manual för {meta ? meta.name : slug}
      </h1>

      {loading && (
        <div className="flex items-center gap-3 text-[#4A4A4A] mt-10 text-lg">
          <Loader2 className="animate-spin" size={28} /> Hämtar manual...
        </div>
      )}

      {!loading && products.length === 0 && (
        <p className="text-lg text-[#4A4A4A] mt-8">Det finns ingen manual publicerad för den här kategorin ännu.</p>
      )}

      <div className="mt-8 space-y-10">
        {products.map((p) => (
          <article key={p.id} data-testid={`product-${p.id}`} className="bg-white border-2 border-gray-300 rounded-xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              {productImage(p) && (
                <img src={productImage(p)} alt={p.title} className="w-full h-64 md:h-full object-cover border-b-2 md:border-b-0 md:border-r-2 border-gray-200" />
              )}
              <div className="p-7 md:p-9">
                <h2 className="font-heading text-3xl font-bold text-[#1A1A1A]">{p.title}</h2>
                {p.subtitle && <p className="text-lg text-[#4A4A4A] mt-2">{p.subtitle}</p>}
                <p className="text-lg leading-relaxed text-[#1A1A1A] mt-4">{p.description}</p>

                {p.features && p.features.length > 0 && (
                  <ul className="mt-5 space-y-3">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-lg text-[#1A1A1A]">
                        <CheckCircle2 className="text-[#0B7A3F] shrink-0 mt-1" size={24} strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                  <span data-testid={`price-${p.id}`} className="font-heading text-3xl font-extrabold text-[#1A1A1A]">
                    {Math.round(p.price)} kr
                  </span>
                  <button
                    data-testid={`buy-${p.id}`}
                    onClick={() => navigate(`/kassa/${p.id}`)}
                    className="inline-flex items-center justify-center gap-3 min-h-[64px] px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors"
                  >
                    <ShoppingCart size={26} strokeWidth={2.5} /> Köp manual här
                  </button>
                </div>

                <ul data-testid={`trust-${p.id}`} className="mt-6 border-t-2 border-gray-100 pt-5 space-y-2.5">
                  <li className="flex items-center gap-3 text-base text-[#4A4A4A]">
                    <Download size={22} strokeWidth={2} className="text-[#0056D2] shrink-0" />
                    Du laddar ner PDF:en direkt efter köpet.
                  </li>
                  <li className="flex items-center gap-3 text-base text-[#4A4A4A]">
                    <Mail size={22} strokeWidth={2} className="text-[#0056D2] shrink-0" />
                    En kopia skickas även till din e-post.
                  </li>
                  <li className="flex items-center gap-3 text-base text-[#4A4A4A]">
                    <FileText size={22} strokeWidth={2} className="text-[#0056D2] shrink-0" />
                    PDF-format – går att läsa på mobil, surfplatta och dator.
                  </li>
                </ul>
              </div>
            </div>

            {p.sample_images && p.sample_images.length > 0 && (
              <div className="border-t-2 border-gray-200 p-7 md:p-9">
                <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] mb-4">Exempel från insidan</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {p.sample_images.map((path, i) => (
                    <img key={i} src={productImage({ sample_images: [path] })} alt={`Exempelsida ${i + 1}`} className="w-full h-56 object-contain bg-gray-50 rounded-lg border-2 border-gray-200" />
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
      </div>

      <ManualInfo content={content} />

      <RequestManual />
    </div>
  );
}
